import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { CloseOutlined } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import { selectIsOrganizationCreated } from "@/store/redux/slices/kyc/kyc_setup_slice";
import "@/assets/styles/home_message.css";

const INACTIVITY_THRESHOLD = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

const HomeMessage = ({ userProfile, isSearching }) => {
  const [visible, setVisible] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);
  const messageRef = useRef(null);

  // Check if organization was just created (KYC signup completion)
  const isOrganizationCreated = useSelector(selectIsOrganizationCreated);

  useEffect(() => {
    // Get current timestamp
    const now = Date.now();

    // Check for various conditions to show the message
    const messageConfig = JSON.parse(
      localStorage.getItem("welcomeMessageConfig") || "{}"
    );
    const lastDismissed = messageConfig.lastDismissed || 0;
    const firstLogin = messageConfig.firstLoginShown !== true;
    const inactivePeriod =
      now - (messageConfig.lastActivity || 0) > INACTIVITY_THRESHOLD;

    // Show message if:
    // 1. First login after registration OR
    // 2. Just created organization OR
    // 3. Returning after long inactivity AND message wasn't dismissed recently
    const shouldShow =
      firstLogin ||
      isOrganizationCreated ||
      (inactivePeriod && now - lastDismissed > INACTIVITY_THRESHOLD);

    if (shouldShow) {
      localStorage.setItem(
        "welcomeMessageConfig",
        JSON.stringify({
          ...messageConfig,
          firstLoginShown: true,
          lastActivity: now,
        })
      );

      // Make message visible with a small delay for initial animation
      setTimeout(() => setVisible(true), 100);
    }

    const updateLastActivity = () => {
      const config = JSON.parse(
        localStorage.getItem("welcomeMessageConfig") || "{}"
      );
      localStorage.setItem(
        "welcomeMessageConfig",
        JSON.stringify({
          ...config,
          lastActivity: Date.now(),
        })
      );
    };

    // Set up activity tracking
    window.addEventListener("click", updateLastActivity);
    window.addEventListener("keypress", updateLastActivity);

    return () => {
      // Remove event listeners on unmount
      window.removeEventListener("click", updateLastActivity);
      window.removeEventListener("keypress", updateLastActivity);
    };
  }, [isOrganizationCreated]);

  // Extract user's full name from the response
  const userFullName = userProfile?.fullname || "Loading...";

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const handleClose = () => {
    // Start fade-out animation
    setAnimateOut(true);

    const messageConfig = JSON.parse(
      localStorage.getItem("welcomeMessageConfig") || "{}"
    );
    localStorage.setItem(
      "welcomeMessageConfig",
      JSON.stringify({
        ...messageConfig,
        lastDismissed: Date.now(),
      })
    );

    // Hide component after animation completes
    setTimeout(() => {
      setVisible(false);
      setAnimateOut(false);
    }, 500); // Match this to the CSS transition duration
  };

  if (!visible) {
    return null; // Don't render anything if message is hidden
  }

  return (
    <div
      ref={messageRef}
      className={`home-message-container h-[140px] relative overflow-hidden flex items-start gap-5 p-3 mb-5 text-white rounded-md bg-primary ${
        animateOut ? "fade-out" : "fade-in"
      }`}>
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all"
        aria-label="Close welcome message">
        <CloseOutlined className="w-4 h-4 text-white" />
      </button>

      <Avatar
        src={userProfile?.profileImage}
        size={70}
        alt={userFullName}
      />

      <div className="flex flex-col items-start">
        <h3 className="text-xs font-bold">{getGreeting()}</h3>
        <h1 className="text-3xl font-bold">
          {isSearching ? "Loading..." : userFullName}
        </h1>
        <p className="text-sm text-left line-clamp-3">
          Welcome to your dashboard. Here you can take a look around and perform
          quick tasks such as previewing your balance, survey reports,
          activities, calendar and more. You can also hide this bar if you want.
        </p>
      </div>
    </div>
  );
};

HomeMessage.propTypes = {
  userProfile: PropTypes.object,
  isSearching: PropTypes.bool,
};

export default HomeMessage;
